import axios, { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig, Method } from 'axios';

export const API_URL = import.meta.env.VITE_API_URL || import.meta.env.REACT_APP_API_URL || 'http://localhost/api/v1';

/**
 * Callback for handling unauthorized (401) responses
 * Set this from the app layer where navigate is available
 * Must return a Promise to allow proper guard reset
 */
let onUnauthorizedCallback: (() => Promise<void>) | null = null;

/**
 * Guard to prevent multiple redirects on repeated 401s
 */
let isHandlingUnauthorized = false;

/**
 * Set the callback to handle unauthorized responses
 * Should be called from AppProviders where navigate is available
 * Callback must return a Promise
 */
export const setUnauthorizedHandler = (callback: () => Promise<void>): void => {
    onUnauthorizedCallback = callback;
};

const getToken = (): string | null => {
    try {
        const tokenString = localStorage.getItem('token');
        if (!tokenString) return null;
        const userToken = JSON.parse(tokenString);
        return userToken;
    } catch (e) {
        return null;
    }
};

// Create axios instance
export const httpClient = axios.create({
    baseURL: API_URL,
    responseType: 'json',
    timeout: 50000,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add token dynamically
httpClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const token = getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle errors
httpClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Prevent multiple redirects on repeated 401s
            if (isHandlingUnauthorized) {
                return Promise.reject(error);
            }

            // Always delegate to callback - do not touch localStorage or decide routes here
            if (onUnauthorizedCallback) {
                isHandlingUnauthorized = true;
                // Call callback and reset guard in finally
                Promise.resolve(onUnauthorizedCallback())
                    .finally(() => {
                        isHandlingUnauthorized = false;
                    })
                    .catch(() => {
                        // Ignore errors from callback
                    });
            }
        }
        return Promise.reject(error);
    }
);

/**
 * Generic API call function
 * @param url - API endpoint
 * @param body - Request body (undefined for GET requests)
 * @param method - HTTP method (default: 'post')
 * @param config - Additional axios config
 * @param headers - Additional headers
 */
export const callApi = async <TResponse = unknown, TBody = unknown>(
    url: string,
    body: TBody | null = null,
    method: Method = 'post',
    config: AxiosRequestConfig = {},
    headers?: Record<string, string>
): Promise<AxiosResponse<TResponse>> => {
    return httpClient({
        method,
        url: url,
        data: body,
        headers: headers,
        ...config,
    });
};
